import { useCallback, useEffect, useRef, useState } from 'react';

interface UseResizableSidebarOptions {
  minWidth?: number;
  maxWidth?: number;
  defaultWidth?: number;
  collapseThreshold?: number;
  storageKey?: string;
  onCollapse?: () => void;
}

export function useResizableSidebar({
  minWidth = 280,
  maxWidth = 600,
  defaultWidth = 320,
  collapseThreshold = 200,
  storageKey = 'guidelines-sidebar-width',
  onCollapse,
}: UseResizableSidebarOptions = {}) {
  // Load persisted width or use default
  const [width, setWidth] = useState(() => {
    const stored = localStorage.getItem(storageKey);
    return stored ? parseInt(stored, 10) : defaultWidth;
  });

  const [isResizing, setIsResizing] = useState(false);
  const startXRef = useRef(0);
  const startWidthRef = useRef(0);

  const handleMouseDown = useCallback(
    (e: React.MouseEvent) => {
      e.preventDefault();
      setIsResizing(true);
      startXRef.current = e.clientX;
      startWidthRef.current = width;
    },
    [width],
  );

  useEffect(() => {
    if (!isResizing) return undefined;

    let hasCollapsed = false;

    const handleMouseMove = (e: MouseEvent) => {
      const delta = startXRef.current - e.clientX; // Reversed because sidebar is on right
      const newWidth = startWidthRef.current + delta;

      // Check if crossing collapse threshold
      if (newWidth <= collapseThreshold && !hasCollapsed) {
        // Trigger collapse immediately
        hasCollapsed = true;
        onCollapse?.();
        // Save the last valid width for when user expands again
        const lastValidWidth = Math.max(startWidthRef.current, minWidth);
        localStorage.setItem(storageKey, lastValidWidth.toString());
        return undefined;
      }

      // Stop at collapse threshold - don't allow dragging smaller
      const clampedWidth = Math.min(
        Math.max(newWidth, collapseThreshold),
        maxWidth,
      );
      setWidth(clampedWidth);
      return undefined;
    };

    const handleMouseUp = () => {
      setIsResizing(false);

      // Only save if not collapsed
      if (!hasCollapsed && width > collapseThreshold) {
        // Normal resize - clamp to valid range and persist
        const validWidth = Math.min(Math.max(width, minWidth), maxWidth);
        setWidth(validWidth);
        localStorage.setItem(storageKey, validWidth.toString());
      }
    };

    document.addEventListener('mousemove', handleMouseMove);
    document.addEventListener('mouseup', handleMouseUp);

    // Add body class to prevent text selection during resize
    document.body.style.cursor = 'col-resize';
    document.body.style.userSelect = 'none';

    return () => {
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
      document.body.style.cursor = '';
      document.body.style.userSelect = '';
    };
  }, [
    isResizing,
    width,
    minWidth,
    maxWidth,
    collapseThreshold,
    storageKey,
    onCollapse,
  ]);

  // Keyboard support for resizing
  useEffect(() => {
    function handleKeyboard(e: KeyboardEvent) {
      // Hold Alt and use arrow keys to resize
      if (e.altKey) {
        if (e.key === 'ArrowLeft') {
          e.preventDefault();
          setWidth((prev) => Math.max(prev - 20, minWidth));
        } else if (e.key === 'ArrowRight') {
          e.preventDefault();
          setWidth((prev) => Math.min(prev + 20, maxWidth));
        }
      }
    }

    window.addEventListener('keydown', handleKeyboard);
    return () => window.removeEventListener('keydown', handleKeyboard);
  }, [minWidth, maxWidth]);

  // Function to restore width from localStorage (for expand from collapsed state)
  const restoreWidth = useCallback(() => {
    const stored = localStorage.getItem(storageKey);
    const restoredWidth = stored ? parseInt(stored, 10) : defaultWidth;
    const validWidth = Math.min(Math.max(restoredWidth, minWidth), maxWidth);
    setWidth(validWidth);
  }, [storageKey, defaultWidth, minWidth, maxWidth]);

  return {
    width,
    isResizing,
    handleMouseDown,
    restoreWidth,
  };
}
